import { Box, Link } from "@mui/material";
import { MouseEvent, ReactNode } from "react";

import type { ScoutToolPart } from "./message/scoutChatMessage";

import {
  firstFriendlyComplianceIdentifier,
  firstFriendlyRegulationCitation,
  formatKnowledgeNodeLookup,
  isKnowledgeNodeId,
  sanitizeApiLookupQuery,
  sanitizeComplianceSearchQuery,
  type KnowledgeNodeLookupParts,
} from "./customerFacingCopy";
import { GRAPHQL_LABELS, INTROSPECTION_SUBJECT } from "./graphqlLabels";
import { parseSandboxArtifactRef, type SandboxArtifactRef } from "./sandboxArtifactRef";
import { SCOUT_ENCAMP_REPRESENTATIVE_TOOL_NAME } from "./scoutEncampRepresentative";
import { PRESENT_SCOUT_PRO_UPSELL_TOOL_NAME } from "./scoutProUpsell";
import { SCOUT_TODO_TOOL_NAMES } from "./scoutTodoSnapshot";
import { unwrapScoutToolPayload } from "./unwrapScoutToolPayload";

export type ToolRenderContext = {
  tool: ScoutToolPart;
  /** True when the tool has been dispatched but output isn't available yet. */
  isRunning: boolean;
};

export type ToolRenderer = (ctx: ToolRenderContext) => ReactNode;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function renderUnderline(children: ReactNode): ReactNode {
  return (
    <Box component="span" sx={{ textDecoration: "underline" }}>
      {children}
    </Box>
  );
}

function readString(value: unknown, key: string): string | null {
  if (isRecord(value) && key in value) {
    const v = value[key];
    if (typeof v === "string" && v.trim().length > 0) return v.trim();
  }
  return null;
}

/**
 * Human article title for get_*_article tool rows.
 * Prefer title/name; never surface corpus node ids (`support:article:…`).
 */
function readOpenArticleTitle(
  tool: Pick<ScoutToolPart, "toolInput" | "toolOutput">
): string | null {
  const output = unwrapScoutToolPayload(tool.toolOutput);
  const candidates = [
    readString(tool.toolInput, "title"),
    readString(output, "title"),
    readString(output, "name"),
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (isKnowledgeNodeId(candidate)) continue;
    return candidate;
  }
  return null;
}

function readObject(value: unknown, key: string): Record<string, unknown> | null {
  if (!isRecord(value) || !(key in value)) {
    return null;
  }

  const candidate = value[key];
  return isRecord(candidate) ? candidate : null;
}

/** First non-empty string value among the given keys. */
function readFirstString(value: unknown, keys: string[]): string | null {
  for (const key of keys) {
    const v = readString(value, key);
    if (v !== null) return v;
  }
  return null;
}

function truncate(value: string, max = 80): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1).trimEnd()}…`;
}

export const TOOL_PARAMETER_MAX_CHARS = 60;

/**
 * Title for a sandbox step, preferring the model's own `label` over the
 * generic fallback copy.
 *
 * The label is model-authored free text, so it gets the same truncation as
 * any other model-supplied string we render. `isRunning` only decides the
 * trailing ellipsis: the model writes one label per call and cannot revise it
 * when the call completes, so the tense it chose is left alone rather than
 * mangled into a past form here.
 */
function renderSandboxLabel(
  tool: ScoutToolPart,
  isRunning: boolean,
  runningFallback: string,
  doneFallback: string
): ReactNode {
  const label = readString(tool.toolInput, "label");
  if (!label) {
    return isRunning ? `${runningFallback}…` : doneFallback;
  }
  const display = truncate(label, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !display.endsWith("…") ? "…" : "";
  return `${display}${trailing}`;
}

/**
 * Short display label for a URL — hostname stripped of `www.`, falling back
 * to a lightweight regex trim if the URL can't be parsed. Paths are dropped;
 * the full URL stays on the clickable anchor via `href`.
 */
function displayUrl(url: string): string {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    if (host.length > 0) return host;
  } catch {
    // fall through
  }
  return url.replace(/^https?:\/\/(www\.)?/i, "").split("/")[0];
}

function stopPropagation(event: MouseEvent<HTMLAnchorElement>) {
  // The enclosing ToolCallGroup row is a click target that toggles expansion;
  // without this, clicking an inline source URL would also collapse/expand.
  event.stopPropagation();
}

/**
 * Extracts the URLs returned by a native provider web_search tool.
 * Handles both shapes:
 *  - Anthropic `webSearch_20250305` → `{ type: "web_search_result", url }[]`
 *  - OpenAI `webSearch` → `{ action?: { url? }, sources?: { type, url }[] }`
 */
function readWebSearchUrls(output: unknown): string[] {
  const urls: string[] = [];
  const push = (value: unknown) => {
    if (typeof value === "string" && value.trim().length > 0) {
      urls.push(value.trim());
    }
  };

  if (Array.isArray(output)) {
    for (const item of output) push(readString(item, "url"));
  } else if (isRecord(output)) {
    const record = output;
    if (record.action && typeof record.action === "object") {
      push(readString(record.action, "url"));
    }
    if (Array.isArray(record.sources)) {
      for (const src of record.sources) push(readString(src, "url"));
    }
  }

  return Array.from(new Set(urls));
}

/**
 * Renders a "Verb <thing> for 'query'" pattern with a running/done verb pair.
 * If no query param is found on the input, falls back to just the phrase.
 */
function searchPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  object: string;
  query: string | null;
  isRunning: boolean;
}): ReactNode {
  const { runningVerb, doneVerb, object, query, isRunning } = options;
  const verb = isRunning ? runningVerb : doneVerb;
  if (!query) {
    return isRunning ? `${verb} ${object}…` : `${verb} ${object}`;
  }
  const displayQuery = truncate(query, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !displayQuery.endsWith("…") ? "…" : "";
  return (
    <>
      {verb} {object} for {renderUnderline(displayQuery)}
      {trailing}
    </>
  );
}

type TodoItemPreview = { id: string | null; task: string | null };

/**
 * Parse a field that may be either a JSON-encoded string (how the agent SDK
 * sends `create_todos.items` / `update_todos.updates`) or an already-decoded
 * array (how tests / direct calls pass it).
 */
function parseJsonArrayField(value: unknown, key: string): Record<string, unknown>[] {
  if (!isRecord(value) || !(key in value)) return [];
  let candidate = value[key];
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(candidate)) return [];
  return candidate.filter(isRecord);
}

function readTodoItems(input: unknown): TodoItemPreview[] {
  return parseJsonArrayField(input, "items").map((item) => ({
    id: readString(item, "id"),
    task: readString(item, "task"),
  }));
}

/** Compact "AL, AK, AZ (+11 more)" summary of todo ids, falling back to count. */
function summarizeTodoItems(items: TodoItemPreview[]): string {
  const ids = items.map((item) => item.id).filter((id): id is string => Boolean(id));
  if (ids.length === 0) return `${items.length} item${items.length === 1 ? "" : "s"}`;
  const shown = ids.slice(0, 6);
  const extra = ids.length - shown.length;
  return `${shown.join(", ")}${extra > 0 ? ` (+${extra} more)` : ""}`;
}

function readTodoSummaryCounts(output: unknown): string | null {
  const summary = readObject(output, "summary");
  if (!summary) return null;
  const readCount = (key: string): number => {
    const value = summary[key];
    return typeof value === "number" && Number.isFinite(value) ? value : 0;
  };
  const pending = readCount("pending") + readCount("in_progress");
  const done = readCount("done");
  const skipped = readCount("skipped");
  const parts: string[] = [];
  if (pending > 0) parts.push(`${pending} pending`);
  if (done > 0) parts.push(`${done} done`);
  if (skipped > 0) parts.push(`${skipped} skipped`);
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * Fallback for any tool that doesn't have a custom renderer.
 * Uses the backend-provided `toolDisplayName` if present; otherwise a neutral
 * customer phrase — never snake_case or auto-humanized tool ids.
 */
const UNKNOWN_TOOL_FALLBACK_LABEL = "Checking information";

const genericRenderer: ToolRenderer = ({ tool, isRunning }) => {
  const label = tool.toolDisplayName ?? UNKNOWN_TOOL_FALLBACK_LABEL;
  return isRunning ? `${label}…` : label;
};

const QUERY_INPUT_KEYS = ["query", "search", "term", "q", "topic"];

function readGraphQLVariables(value: unknown): Record<string, unknown> | null {
  return readObject(value, "variables");
}

/**
 * Read the first top-level FIELD name selected in a GraphQL query — i.e. the
 * actual entity being queried, not the model-invented operation name.
 *
 * For `query PickCaFacility { facility(id: $id) { ... } }` this returns
 * `"facility"` rather than `"PickCaFacility"`. Field names are conventionally
 * stable lowerCamelCase from the schema, so they map cleanly to the curated
 * GRAPHQL_LABELS table. Operation names are whatever the model felt like
 * writing, so we don't trust them.
 *
 * GraphQL aliases are stripped: `c1: reportingFacilityChemicals(...)` returns
 * `"reportingFacilityChemicals"` not `"c1"`.
 */
function readGraphQLTopLevelField(value: unknown): string | null {
  const query = readString(value, "query");
  if (!query) return null;

  // Strip top-level operation header like `query Foo($x: Int) ` /
  // `mutation Bar { ` so the first field token is what we land on.
  const stripped = query.replace(
    /^\s*(?:query|mutation|subscription)\s+\w*\s*(?:\([^)]*\))?\s*/u,
    ""
  );

  // Match the first selection inside the leading `{ ... }`. Two shapes:
  //   { fieldName ...      → group 2 is the field
  //   { alias: fieldName … → group 2 is alias, group 3 is the field
  // Aliases are common when the model batches multiple calls into one query.
  const match = stripped.match(
    /^\s*\{\s*([A-Za-z_][A-Za-z0-9_]*)(?:\s*:\s*([A-Za-z_][A-Za-z0-9_]*))?/u
  );
  if (!match) return null;
  // If group 2 (real field after alias) matched, prefer it over the alias.
  return match[2] ?? match[1] ?? null;
}

const INTROSPECTION_FIELDS = new Set(["__type", "__schema", "__typename"]);

function isIntrospectionQuery(toolInput: unknown): boolean {
  const field = readGraphQLTopLevelField(toolInput);
  if (field && INTROSPECTION_FIELDS.has(field)) return true;

  // Fallback: catch queries where the model nests __type/__schema deeper.
  const query = readString(toolInput, "query");
  if (!query) return false;
  return /\b__(?:type|schema)\s*\(/.test(query);
}

function describeGraphQLOperation(toolInput: unknown): string {
  if (isIntrospectionQuery(toolInput)) {
    return INTROSPECTION_SUBJECT;
  }

  // The curated map is keyed by the actual GraphQL field, not the
  // model-invented operation name. Fields are stable lowerCamelCase from the
  // schema; operation names are whatever the model wrote.
  const field = readGraphQLTopLevelField(toolInput);
  if (field) {
    const label = GRAPHQL_LABELS[field];
    if (label) {
      const variables = readGraphQLVariables(toolInput);
      const search = readString(variables, "search");
      const refined = label.refine?.(variables, search);
      return refined ?? label.subject;
    }
  }

  // Unknown field → don't render the model's invented operation name (we
  // used to fall through to humanize() but it produced ugly things like
  // "pick ca facility"). Stay generic.
  return "Encamp data";
}

function summarizeGraphQLExecution(toolInput: unknown): string {
  // describeGraphQLOperation handles introspection, field-based lookup in
  // the curated map (with optional refine() based on search/variables), and
  // the generic fallback. Keep this thin so the renderer logic stays in one
  // place.
  return describeGraphQLOperation(toolInput);
}

function graphQLLookupPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  subject: string;
  isRunning: boolean;
}): string {
  const verb = options.isRunning ? options.runningVerb : options.doneVerb;
  const suffix = options.isRunning ? "…" : "";
  return `${verb} ${options.subject}${suffix}`;
}

/**
 * Renders a "Verb <object> [*citation*]" pattern for get-by-id tools.
 * Falls back to just the phrase when no citation is found on the input.
 */
function lookupPhrase(options: {
  runningVerb: string;
  doneVerb: string;
  object: string;
  citation: string | null;
  isRunning: boolean;
}): ReactNode {
  const { runningVerb, doneVerb, object, citation, isRunning } = options;
  const verb = isRunning ? runningVerb : doneVerb;
  if (!citation) {
    return isRunning ? `${verb} ${object}…` : `${verb} ${object}`;
  }
  const displayCitation = truncate(citation, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !displayCitation.endsWith("…") ? "…" : "";
  return (
    <>
      {verb} {renderUnderline(displayCitation)}
      {trailing}
    </>
  );
}

/**
 * "Reading federal regulations for Section 370.10" — used when the tool was
 * invoked with a corpus node id and we have no friendlier citation yet.
 */
function readingKnowledgePhrase(parts: KnowledgeNodeLookupParts, isRunning: boolean): ReactNode {
  const verb = "Reading";
  if (!parts.refLabel) {
    return isRunning ? `${verb} ${parts.corpusLabel}…` : `${verb} ${parts.corpusLabel}`;
  }
  const displayRef = truncate(parts.refLabel, TOOL_PARAMETER_MAX_CHARS);
  const trailing = isRunning && !displayRef.endsWith("…") ? "…" : "";
  return (
    <>
      {verb} {parts.corpusLabel} for {renderUnderline(displayRef)}
      {trailing}
    </>
  );
}

function readRegulationCitationDisplay(toolOutput: unknown): string | null {
  const source = readObject(toolOutput, "source");
  const citation = source ? readObject(source, "citation") : null;
  if (citation) {
    const display = readString(citation, "display");
    if (display) return display;
  }
  // Local in-process get_regulation returns RegulationContext.citation.
  return readString(toolOutput, "citation");
}

/**
 * Prefer a human citation; otherwise format a knowledge node id as
 * "Reading {corpus} for {Section …}".
 */
function regulationGetPhrase(options: {
  tool: ScoutToolPart;
  isRunning: boolean;
  fallbackObject: string;
}): ReactNode {
  const { tool, isRunning, fallbackObject } = options;
  const friendly = firstFriendlyRegulationCitation([
    readString(tool.toolInput, "citation"),
    readString(tool.toolInput, "section"),
    readRegulationCitationDisplay(tool.toolOutput),
    readString(tool.toolOutput, "title"),
    readString(tool.toolOutput, "name"),
  ]);
  if (friendly) {
    return lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: fallbackObject,
      citation: friendly,
      isRunning,
    });
  }

  const nodeId = readFirstString(tool.toolInput, ["id", "nodeId"]);
  const formatted = nodeId ? formatKnowledgeNodeLookup(nodeId) : null;
  if (formatted) {
    return readingKnowledgePhrase(formatted, isRunning);
  }

  return lookupPhrase({
    runningVerb: "Looking up",
    doneVerb: "Looking up",
    object: fallbackObject,
    citation: null,
    isRunning,
  });
}

const CITATION_INPUT_KEYS = ["citation", "section", "id", "nodeId"];

const toolRenderers: Record<string, ToolRenderer> = {
  search_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "federal regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  search_ca_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "California regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  search_ny_regulations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "New York regulations",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  browse_federal_regulations: ({ isRunning }) =>
    isRunning ? "Browsing federal regulations…" : "Browsing federal regulations",

  browse_ca_regulations: ({ isRunning }) =>
    isRunning ? "Browsing California regulations…" : "Browsing California regulations",

  browse_ny_regulations: ({ isRunning }) =>
    isRunning ? "Browsing New York regulations…" : "Browsing New York regulations",

  get_regulation: ({ tool, isRunning }) =>
    regulationGetPhrase({ tool, isRunning, fallbackObject: "federal regulation" }),

  get_ca_regulation: ({ tool, isRunning }) =>
    regulationGetPhrase({ tool, isRunning, fallbackObject: "California regulation" }),

  get_ny_regulation: ({ tool, isRunning }) =>
    regulationGetPhrase({ tool, isRunning, fallbackObject: "New York regulation" }),

  search_compliance: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "compliance requirements",
      query: sanitizeComplianceSearchQuery(readFirstString(tool.toolInput, QUERY_INPUT_KEYS)),
      isRunning,
    }),

  exhaustive_search_compliance: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "compliance requirements",
      query: sanitizeComplianceSearchQuery(
        readFirstString(tool.toolInput, ["semanticSearchQuery", ...QUERY_INPUT_KEYS])
      ),
      isRunning,
    }),

  search_epa: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "EPA guidance",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  get_epa_article: ({ tool, isRunning }) => {
    return lookupPhrase({
      runningVerb: "Opening",
      doneVerb: "Opening",
      object: "EPA article",
      citation: readOpenArticleTitle(tool),
      isRunning,
    });
  },

  get_compliance_requirement: ({ tool, isRunning }) => {
    const identifier = firstFriendlyComplianceIdentifier([
      readString(tool.toolInput, "title"),
      readString(tool.toolOutput, "title"),
      readString(tool.toolInput, "name"),
      readString(tool.toolOutput, "name"),
      readFirstString(tool.toolInput, CITATION_INPUT_KEYS),
    ]);
    return lookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      object: "compliance requirement",
      citation: identifier,
      isRunning,
    });
  },

  search_operations: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "API documentation",
      query: sanitizeApiLookupQuery(readFirstString(tool.toolInput, QUERY_INPUT_KEYS)),
      isRunning,
    }),

  get_operation: ({ tool, isRunning }) => {
    const operationName =
      readString(tool.toolInput, "name") ??
      readString(tool.toolOutput, "title") ??
      readString(tool.toolInput, "nodeId");
    const lookupName = operationName
      ? describeGraphQLOperation({
          query: `query Probe { ${operationName} { __typename } }`,
        })
      : "API documentation";
    return graphQLLookupPhrase({
      runningVerb: "Reading",
      doneVerb: "Reading",
      subject: lookupName === "Encamp data" ? "API documentation" : `the ${lookupName} reference`,
      isRunning,
    });
  },

  execute_graphql: (ctx) => {
    const { tool, isRunning } = ctx;
    // Always use `graphQLLookupPhrase` for execute_graphql, even before the
    // query has streamed in. `describeGraphQLOperation` returns the neutral
    // subject "Encamp data" when the top-level field can't be determined,
    // so the row reads "Looking up Encamp data…" during input streaming
    // and refines to "Looking up facilities in CA…" once the query is
    // parseable. Keeping the verb stable across the swap eliminates the
    // jarring "Searching your Encamp data" → "Looking up facilities in CA"
    // flicker that came from falling through to genericRenderer (which
    // used the server-set placeholder displayName in mid-streaming state).
    return graphQLLookupPhrase({
      runningVerb: "Looking up",
      doneVerb: "Looking up",
      subject: summarizeGraphQLExecution(tool.toolInput),
      isRunning,
    });
  },

  search_support: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "support articles",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  get_support_article: ({ tool, isRunning }) => {
    // Prefer a human title; never fall back to nodeId (support:article:…).
    // Local tools return `title`; knowledge-service getNode returns `name`
    // (remote projection also aliases `title`).
    return lookupPhrase({
      runningVerb: "Opening",
      doneVerb: "Opening",
      object: "support article",
      citation: readOpenArticleTitle(tool),
      isRunning,
    });
  },

  search_knowledge: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "the knowledge base",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  get_article: ({ tool }) => {
    const title = readOpenArticleTitle(tool);
    const verb = "Opening";
    if (!title) return `${verb} knowledge base article`;
    return (
      <>
        {verb} {renderUnderline(truncate(title, 60))}
      </>
    );
  },

  search_code: ({ tool, isRunning }) =>
    searchPhrase({
      runningVerb: "Searching",
      doneVerb: "Searching",
      object: "code",
      query: readFirstString(tool.toolInput, QUERY_INPUT_KEYS),
      isRunning,
    }),

  web_fetch: ({ tool }) => {
    const url = readString(tool.toolInput, "url");
    if (!url) return "Fetching web page";
    return <>Fetching {renderUnderline(truncate(url, 60))}</>;
  },

  create_todos: ({ tool, isRunning }) => {
    const items = readTodoItems(tool.toolInput);
    if (items.length === 0) {
      return isRunning ? "Planning the steps…" : "Planning the steps";
    }
    return (
      <>
        Planning {items.length} step{items.length === 1 ? "" : "s"}:{" "}
        {renderUnderline(truncate(summarizeTodoItems(items), TOOL_PARAMETER_MAX_CHARS))}
      </>
    );
  },

  update_todo: ({ tool }) => {
    const id = readString(tool.toolInput, "id");
    const status = readString(tool.toolInput, "status");
    if (id && status) {
      return (
        <>
          Updating {renderUnderline(id)} → {status.replace(/_/g, " ")}
        </>
      );
    }
    if (id) return <>Updating {renderUnderline(id)}</>;
    return "Updating a step";
  },

  update_todos: ({ tool }) => {
    const updates = parseJsonArrayField(tool.toolInput, "updates");
    if (updates.length === 0) return "Updating the checklist";
    return `Updating ${updates.length} step${updates.length === 1 ? "" : "s"}`;
  },

  get_todos: ({ tool, isRunning }) => {
    if (isRunning) return "Reviewing the checklist…";
    const counts = readTodoSummaryCounts(tool.toolOutput);
    return counts ? `Reviewing the checklist (${counts})` : "Reviewing the checklist";
  },

  write_plan: ({ isRunning }) => (isRunning ? "Writing the plan…" : "Writing the plan"),

  read_plan: ({ isRunning }) => (isRunning ? "Reviewing the plan…" : "Reviewing the plan"),

  show_plan: ({ isRunning }) => (isRunning ? "Showing the plan…" : "Showing the plan"),

  file_write: ({ isRunning }) => (isRunning ? "Saving findings…" : "Saving findings"),

  file_append: ({ isRunning }) => (isRunning ? "Updating findings…" : "Updating findings"),

  file_read: ({ isRunning }) => (isRunning ? "Reviewing findings…" : "Reviewing findings"),

  show_file: ({ isRunning }) => (isRunning ? "Showing findings…" : "Showing findings"),

  // Sandbox copy is deliberately non-technical: these render in the customer
  // chat, where filenames like `inspect.py`, shell commands, and stderr
  // redirects read as noise at best and as Scout flailing at worst (a
  // `mv x.py y.py && python3 y.py` retry looks like an error to a compliance
  // manager). The two moments a customer actually cares about — their file
  // going in and their deliverable coming out — keep their real filenames,
  // because those are names the customer recognizes. Everything in between
  // describes intent, matching the planning tools' "Saving findings" voice
  // and the same no-paths-no-tool-names rule the model follows for its own
  // customer-facing checklist labels.
  //
  // The model supplies that intent itself via the tools' optional `label`
  // param — it knows why it is writing a given script, which no amount of
  // filename inspection here could recover. The constants below are the
  // fallback for calls that omit it (older history, or a turn where the model
  // just didn't set it), so the row is never blank.
  // Fallbacks mirror the label split the tool descriptions ask for: the write
  // is preparation, the run that follows is the work. Keeping the fallbacks
  // distinct means an unlabelled pair still reads as two different steps
  // rather than the same sentence twice.
  sandbox_write_file: ({ tool, isRunning }) =>
    renderSandboxLabel(tool, isRunning, "Preparing the next step", "Prepared the next step"),

  sandbox_run: ({ tool, isRunning }) =>
    renderSandboxLabel(tool, isRunning, "Analyzing your data", "Analyzed your data"),

  sandbox_list_files: ({ isRunning }) =>
    isRunning ? "Checking the workspace…" : "Checked the workspace",

  sandbox_read_file: ({ isRunning }) => (isRunning ? "Checking results…" : "Checked results"),

  sandbox_import_file: ({ isRunning }) => (isRunning ? "Loading your data…" : "Loaded your data"),

  sandbox_import_document: ({ tool, isRunning }) => {
    if (isRunning) return "Loading your document…";
    const filename = readString(tool.toolOutput, "filename");
    return filename ? (
      <>Loaded {renderUnderline(truncate(filename, TOOL_PARAMETER_MAX_CHARS))}</>
    ) : (
      "Loaded your document"
    );
  },

  sandbox_export_file: ({ tool, isRunning }) => {
    const filename =
      readString(tool.toolOutput, "filename") ?? readString(tool.toolInput, "filename");
    const suffix = filename ? (
      <> {renderUnderline(truncate(filename, TOOL_PARAMETER_MAX_CHARS))}</>
    ) : (
      <> your file</>
    );
    return (
      <>
        {isRunning ? "Preparing" : "Prepared"}
        {suffix} for download{isRunning ? "…" : ""}
      </>
    );
  },

  web_search: ({ tool, isRunning }) => {
    const query = readFirstString(tool.toolInput, QUERY_INPUT_KEYS);
    const displayQuery = query ? truncate(query, TOOL_PARAMETER_MAX_CHARS) : null;

    if (isRunning) {
      return displayQuery ? (
        <>
          Searching the web for {renderUnderline(displayQuery)}
          {!displayQuery.endsWith("…") ? "…" : ""}
        </>
      ) : (
        "Searching the web…"
      );
    }

    const urls = readWebSearchUrls(tool.toolOutput);
    if (urls.length === 0) {
      return displayQuery ? (
        <>Searching the web for {renderUnderline(displayQuery)}</>
      ) : (
        "Searching the web"
      );
    }

    const shown = urls.slice(0, 3);
    const extra = urls.length - shown.length;
    return (
      <>
        Searching{" "}
        {shown.map((url, idx) => (
          <Box
            component="span"
            key={url}
            sx={{ whiteSpace: "nowrap" }}
            data-testid="web-search-source"
          >
            {idx > 0 ? ", " : ""}
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stopPropagation}
              color="inherit"
              underline="always"
            >
              {displayUrl(url)}
            </Link>
          </Box>
        ))}
        {extra > 0 ? ` (+${extra} more)` : ""}
      </>
    );
  },
};

export function renderToolTitle(ctx: ToolRenderContext): ReactNode {
  const renderer = toolRenderers[ctx.tool.toolName] ?? genericRenderer;
  return renderer(ctx);
}

export const SANDBOX_EXPORT_TOOL_NAME = "sandbox_export_file";

export type SandboxExportDownload = {
  key: string;
  artifactRef: SandboxArtifactRef;
  filename: string | null;
};

/**
 * Completed sandbox exports in a group, as download descriptors.
 *
 * Deliberately not part of `renderToolTitle`: a download button is the one
 * control in a tool trace the user is meant to act on, so it renders as a
 * sibling of the group header rather than inside it. The header is a
 * `role="button"` toggle and its detail rows are behind a `Collapse`, so a
 * button nested in either would be a click target inside a click target, or
 * hidden until expanded.
 */
export function readSandboxExportDownloads(tools: ScoutToolPart[]): SandboxExportDownload[] {
  const downloads: SandboxExportDownload[] = [];
  for (const tool of tools) {
    if (tool.toolName !== SANDBOX_EXPORT_TOOL_NAME) continue;
    if (tool.toolState === "output_error" || tool.toolOutput === undefined) continue;
    // The result carries a storage key, not a URL — the button mints a fresh
    // download URL per click. Legacy results carried a presigned URL;
    // parseSandboxArtifactRef recovers the key from its path, so old exports
    // stay downloadable long after their embedded signatures died.
    const artifactRef = parseSandboxArtifactRef(unwrapScoutToolPayload(tool.toolOutput));
    if (!artifactRef) continue;
    downloads.push({
      key: tool.id,
      artifactRef,
      filename:
        readString(unwrapScoutToolPayload(tool.toolOutput), "filename") ??
        readString(tool.toolInput, "filename"),
    });
  }
  return downloads;
}

/**
 * Tools whose trace row is never shown because the tool is surfaced elsewhere
 * in the message (e.g. the Scout Pro upsell renders as a `ScoutProUpsellCard`),
 * so a row would only duplicate it.
 */
const ALWAYS_HIDDEN_TOOL_NAMES = new Set<string>([
  PRESENT_SCOUT_PRO_UPSELL_TOOL_NAME,
  SCOUT_ENCAMP_REPRESENTATIVE_TOOL_NAME,
]);

export type ToolRowVisibilityContext = {
  /**
   * True when the owning message renders the checklist UI, making the todo
   * planning tools' rows redundant.
   */
  hideTodoToolRows: boolean;
  /**
   * True in staff Observe debug surfaces that show raw tool I/O. When false
   * (customer chat), hide hallucinated tool failures that have no curated
   * renderer and no backend display name.
   */
  showUnknownToolErrors?: boolean;
};

/**
 * Whether a tool call should render as a row in a `ToolCallGroup`. This is the
 * single source of truth for tool-row visibility — kept beside `renderToolTitle`
 * so "how a tool appears in the trace" (its title and whether it appears at all)
 * lives in one place rather than being split across the consumer.
 */
export function isToolRowVisible(
  tool: Pick<ScoutToolPart, "toolName" | "toolState" | "toolDisplayName">,
  { hideTodoToolRows, showUnknownToolErrors = false }: ToolRowVisibilityContext
): boolean {
  if (ALWAYS_HIDDEN_TOOL_NAMES.has(tool.toolName)) return false;
  if (hideTodoToolRows && SCOUT_TODO_TOOL_NAMES.has(tool.toolName)) return false;
  if (
    !showUnknownToolErrors &&
    tool.toolState === "output_error" &&
    !(tool.toolName in toolRenderers) &&
    !tool.toolDisplayName
  ) {
    return false;
  }
  return true;
}
